// Shared types + defaults + seed/starter data for the Partner Portal. The real
// data layer is `src/data/brands-api.ts` (typed GraphQL, persisted since #4782);
// this file holds the UI types (Theme/Brand/Terms), token defs and default
// palettes those operations map to. NOTE: BRAND_STARTERS below is superseded on
// the Theming page by engine-generated candidates (see `src/data/theme-engine/`);
// it still seeds the create-brand starter picker.

/** pop.in brand DNA (from the landing page). */
export const POPIN = {
  magenta: "#e0379a",
  violet: "#7c3aed",
  blue: "#4f6ef7",
  gradient: "linear-gradient(100deg, #e0379a 0%, #7c3aed 52%, #4f6ef7 100%)",
} as const;

/** A named brand theme a partner edits (drives the live preview). A brand can
    have several; one is marked live via `Brand.activeThemeId`. */
export interface Theme {
  id: string;
  name: string;
  header: string;
  background: string;
  surface: string;
  primary: string;
  accent: string;
  text: string;
  logoText: string;
  logoImg: string | null;
}

/** Terminology the partner can rename. */
export interface Terms {
  currencySingular: string;
  currencyPlural: string;
  currencyIcon: string;
  walletLabel: string;
  rewardLabel: string;
}

export interface Brand {
  id: string;
  name: string;
  initials: string;
  plan: string;
  /** All themes belonging to the brand (always at least one). */
  themes: Theme[];
  /** Id of the theme currently live in the app (one of `themes`). */
  activeThemeId: string;
  terms: Terms;
  /** The calling user's role on this brand. Drives owner-only actions (delete).
   *  `null` means the viewer isn't a member — i.e. a super admin viewing a brand
   *  that isn't one of their own. */
  viewerRole?: "OWNER" | "MEMBER" | null;
  /** Display name of the brand's owner (the OWNER member), when known. Used to
   *  attribute a brand a super admin is viewing but doesn't belong to. */
  ownerName?: string | null;
}

/** Shape submitted from the "Create a brand" page. `theme` seeds the brand's
    first ("Default Theme"); the brand's `themes`/`activeThemeId` are derived from
    it on commit. */
export type NewBrandInput = {
  name: string;
  initials: string;
  theme: Theme;
  terms: Terms;
};

/** A starter palette offered when creating a brand. */
export interface BrandStarter {
  id: string;
  name: string;
  desc: string;
  theme: Pick<Theme, "header" | "background" | "surface" | "primary" | "accent" | "text">;
}

export type TokenGroup = "Brand" | "Surfaces" | "Actions";

export interface TokenDef {
  id: string;
  label: string;
  kind: "color" | "logo";
  group: TokenGroup;
  hint: string;
}

/** Data-driven token list so future variables drop in trivially. */
export const TOKEN_DEFS: TokenDef[] = [
  { id: "logo", label: "Logo", kind: "logo", group: "Brand", hint: "Wordmark shown in the app header." },
  { id: "header", label: "Header", kind: "color", group: "Surfaces", hint: "Top navigation bar background." },
  { id: "background", label: "Background", kind: "color", group: "Surfaces", hint: "Main app canvas behind content." },
  { id: "surface", label: "Card surface", kind: "color", group: "Surfaces", hint: "Panels, tiles and sheets." },
  { id: "primary", label: "Primary", kind: "color", group: "Actions", hint: "Primary buttons & key actions." },
  { id: "accent", label: "Accent", kind: "color", group: "Actions", hint: "Highlights, glows & gradients." },
  { id: "text", label: "Text", kind: "color", group: "Actions", hint: "Default foreground text on background." },
];

/** Starter palettes offered on the "Create a brand" page. Each pre-fills the
    theme so a new brand never lands on a blank canvas. */
export const BRAND_STARTERS: BrandStarter[] = [
  {
    id: "popin",
    name: "pop.in Default",
    desc: "Our signature violet & magenta.",
    theme: { header: "#140d33", background: "#0a0820", surface: "#1c1240", primary: "#7c3aed", accent: "#e0379a", text: "#f1ecff" },
  },
  {
    id: "stellar",
    name: "Midnight Mint",
    desc: "Deep navy with a mint primary.",
    theme: { header: "#0c1a2e", background: "#081320", surface: "#11263f", primary: "#11d4a4", accent: "#f5b945", text: "#eafaf4" },
  },
  {
    id: "ember",
    name: "Ember",
    desc: "Warm charcoal with a coral pop.",
    theme: { header: "#241312", background: "#1a0f0e", surface: "#311a18", primary: "#ff5d5d", accent: "#f5b945", text: "#fbeae6" },
  },
  {
    id: "blank",
    name: "Clean Slate",
    desc: "Neutral greys — start from scratch.",
    theme: { header: "#1b1b22", background: "#101015", surface: "#1f1f29", primary: "#5b6cff", accent: "#9aa3b2", text: "#eef0f5" },
  },
];

/** Quick-pick brand palette swatches. */
export const SWATCHES = [
  "#e0379a", "#7c3aed", "#4f6ef7", "#0a0820", "#140d33",
  "#11d4a4", "#f5b945", "#ff5d5d", "#ffffff", "#1b1b26",
];

/** Monotonic id generator for runtime-created brands/themes (mock only). */
let idSeq = 0;
export function genId(prefix: string): string {
  idSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${idSeq}`;
}

export function makeTheme(overrides: Partial<Theme> = {}): Theme {
  return {
    id: genId("theme"),
    name: "Default Theme",
    header: "#140d33",
    background: "#0a0820",
    surface: "#1c1240",
    primary: "#7c3aed",
    accent: "#e0379a",
    text: "#f1ecff",
    logoText: "pop.in",
    logoImg: null,
    ...overrides,
  };
}

export function makeTerms(overrides: Partial<Terms> = {}): Terms {
  return {
    currencySingular: "pop",
    currencyPlural: "pops",
    currencyIcon: "✦",
    walletLabel: "Wallet",
    rewardLabel: "Reward",
    ...overrides,
  };
}

/** Seed brands (multiple per partner). Nova ships two themes to showcase the
    multi-theme UI; Stellar starts with just its default. */
export const BRANDS: Brand[] = [
  {
    id: "nova",
    name: "Nova Play",
    initials: "NP",
    plan: "White-label · Active",
    themes: [
      makeTheme({ id: "nova-default", name: "Default Theme", logoText: "Nova Play" }),
      makeTheme({
        id: "nova-holiday",
        name: "Holiday",
        header: "#0c2a1e",
        background: "#08201a",
        surface: "#11402f",
        primary: "#11d48a",
        accent: "#f5b945",
        text: "#eafaf0",
        logoText: "Nova Play",
      }),
    ],
    activeThemeId: "nova-default",
    terms: makeTerms({}),
  },
  {
    id: "stellar",
    name: "Stellar Games",
    initials: "SG",
    plan: "White-label · Active",
    themes: [
      makeTheme({
        id: "stellar-default",
        name: "Default Theme",
        header: "#0c1a2e",
        background: "#081320",
        surface: "#11263f",
        primary: "#11d4a4",
        accent: "#f5b945",
        text: "#eafaf4",
        logoText: "stellar",
      }),
    ],
    activeThemeId: "stellar-default",
    terms: makeTerms({
      currencySingular: "star",
      currencyPlural: "stars",
      currencyIcon: "★",
      rewardLabel: "Bonus",
    }),
  },
];

export const clone = <T,>(o: T): T => JSON.parse(JSON.stringify(o));
